import React, { useState, useEffect, useRef } from "react";
import "../../styles/IndustriesChips.css";

const industries = [
  "Banking & Insurance",
  "Energy",
  "Healthcare",
  "Aeronautics",
  "Telecommunications",
  "Transport & Logistics",
  "Retail & E-commerce",
  "Public Sector & Government",
  "Manufacturing & Industry",
  "Education & Research",
  "Information Technology & Software Development",
  "Defense & Security",
  "Media & Entertainment",
];

const industryDescriptions = {
  "Banking & Insurance":
    "Digital payments, fraud detection, and AI-driven risk management are reshaping finance. Secure architectures ensure trust while enabling seamless customer experiences.",
  Energy:
    "Smart grids, predictive maintenance, and clean energy innovations drive sustainable operations. Technology optimizes efficiency while meeting global energy demands.",
  Healthcare:
    "Patient-centered apps, EHR integrations, and telemedicine platforms enhance care delivery. Security and compliance remain at the core of every digital solution.",
  Aeronautics:
    "Advanced simulations, safety monitoring, and predictive analytics support aviation. Data-driven systems improve fleet performance and operational efficiency.",
  Telecommunications:
    "5G networks, customer experience platforms, and cloud-native infrastructures empower telecom providers to scale faster and serve smarter.",
  "Transport & Logistics":
    "Real-time tracking, route optimization, and predictive logistics streamline supply chains. Innovation ensures goods move faster, safer, and more efficiently.",
  "Retail & E-commerce":
    "Personalized shopping, omni-channel platforms, and AI recommendations transform customer journeys. Engagement and sales grow through tailored experiences.",
  "Public Sector & Government":
    "Citizen portals, data-driven policy tools, and secure infrastructures modernize governance. Digital services build transparency and trust with communities.",
  "Manufacturing & Industry":
    "IoT automation, predictive quality control, and digital twins revolutionize production lines. Factories become more efficient and resilient with smart technologies.",
  "Education & Research":
    "E-learning platforms, virtual classrooms, and research management tools expand access. Digital innovation accelerates both teaching and discovery.",
  "Information Technology & Software Development":
    "Cloud-native solutions, DevOps pipelines, and scalable architectures power modern applications. Collaboration fuels innovation in fast-paced IT landscapes.",
  "Defense & Security":
    "AI-enabled threat detection, secure communications, and mission-critical systems safeguard national interests. Advanced technologies strengthen resilience.",
  "Media & Entertainment":
    "Streaming platforms, immersive AR/VR, and AI audience analytics transform how content is created, delivered, and consumed worldwide.",
};

export default function IndustriesChips() {
  const [active, setActive] = useState(industries[0]);
  const [paused, setPaused] = useState(false);
  const pausedRef = useRef(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    const scroll = () => {
      if (!pausedRef.current) {
        container.scrollLeft += 1;

        // If reached end of first list, reset smoothly
        if (container.scrollLeft >= container.scrollWidth / 2) {
          container.scrollLeft = 0;
        }

        // 🔹 Find middle chip
        const containerCenter = container.offsetWidth / 2;
        const chips = container.querySelectorAll(".chip");
        let closestChip = null;
        let minDistance = Infinity;

        chips.forEach((chip) => {
          const rect = chip.getBoundingClientRect();
          const containerRect = container.getBoundingClientRect();
          const chipCenter = rect.left - containerRect.left + rect.width / 2;
          const distance = Math.abs(chipCenter - containerCenter);

          if (distance < minDistance) {
            minDistance = distance;
            closestChip = chip.innerText;
          }
        });

        if (closestChip && closestChip !== active) {
          setActive(closestChip);
        }
      }
    };

    const interval = setInterval(scroll, 30);
    return () => clearInterval(interval);
  }, [active]);

  const handleMouseEnter = () => {
    setPaused(true);
    pausedRef.current = true;
  };

  const handleMouseLeave = () => {
    setPaused(false);
    pausedRef.current = false;
  };

  return (
    <div className="industries" id="industries">
      <h2 className="industries__title">Industries we serve</h2>

      <div
        className="industries__chips-wrapper"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {/* ✅ Duplicate industries for infinite loop */}
        <div className="industries__chips" ref={scrollRef}>
          {[...industries, ...industries].map((name, i) => (
            <button
              key={`${name}-${i}`}
              onClick={() => setActive(name)}
              className={`chip ${active === name ? "active" : ""}`}
            >
              {name}
            </button>
          ))}
        </div>
      </div>

      <div className="industries__card">
        <h3>{active}</h3>
        <p>{industryDescriptions[active]}</p>
      </div>
    </div>
  );
}
